import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { motion } from "motion/react"

import Loader from "../../components/Loader"
import { Cross } from "../../components/svgCollection"
import { Hall } from "../buy/Hall"
import { SpiderManSeatIcon } from "../buy/SpiderManSeatIcon"
import { useRequest } from "../../hooks/useRequest"
import { isSpiderManFilm } from "../../lib/spiderManFilm"
import { Place, Session } from "../../types/entities/session"
import { TicketSeat } from "../../types/entities/ticket"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type Props = {
    cinemaId: number
    sessionId: number
    filmName: string
    hallName: string
    ticketSeats: TicketSeat[]
    onClose: () => void
}

function matchTicketSeats(places: Place[], ticketSeats: TicketSeat[]): Place[] {
    return ticketSeats
        .map(booked => places.find(p => p.row === booked.row && p.place === booked.place))
        .filter((p): p is Place => p != null)
}

export default function TicketHallModal({
    cinemaId,
    sessionId,
    filmName,
    hallName,
    ticketSeats,
    onClose,
}: Props) {
    const { request } = useRequest()
    const [loading, setLoading] = useState(true)
    const [sessionData, setSessionData] = useState<Session | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [onClose])

    useEffect(() => {
        let cancelled = false

        const fetchSession = async () => {
            setLoading(true)
            setError(null)
            try {
                const data: Session = await request("GET", `cinema/${cinemaId}/session/${sessionId}`)
                if (!cancelled) setSessionData(data)
            } catch (e) {
                if (!cancelled) {
                    setSessionData(null)
                    setError(e instanceof Error ? e.message : "Не удалось загрузить схему зала")
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchSession()
        return () => {
            cancelled = true
        }
        // request пересоздаётся на каждом рендере — не включаем в deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinemaId, sessionId])

    const spiderMan = isSpiderManFilm(sessionData?.film_name ?? filmName)
    const level = sessionData?.hall.levels?.[0]
    const highlightedSeats = useMemo(() => {
        if (!level) return []
        return matchTicketSeats(level.places, ticketSeats)
    }, [level, ticketSeats])

    const date = sessionData
        ? new Date(sessionData.datetime.replace(" ", "T")).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })
        : ""
    const time = sessionData?.datetime.slice(11, 16) ?? ""

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end justify-center md:items-center"
            style={{ background: "rgba(10,18,42,0.85)", backdropFilter: "blur(8px)" }}
            onClick={onClose}>
            <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 24, opacity: 0 }}
                transition={{ type: "spring", stiffness: 320, damping: 28 }}
                className="flex max-h-[92svh] w-full flex-col overflow-hidden rounded-t-[24px] border border-white/12 md:max-w-2xl md:rounded-[24px]"
                style={{ background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)" }}
                onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4 md:px-6">
                    <div className="min-w-0">
                        <h3
                            className="text-white leading-snug line-clamp-2"
                            style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17 }}>
                            {filmName}
                        </h3>
                        <p className="mt-1 text-white/60 text-[13px]" style={{ fontFamily: BODY }}>
                            {sessionData?.cinema_name ?? ""}
                            {hallName ? ` · Зал ${hallName}` : ""}
                            {date ? ` · ${date}, ${time}` : ""}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 cursor-pointer rounded-full p-1 text-white/70 transition-colors hover:text-white"
                        aria-label="Закрыть">
                        <Cross />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-5 md:px-6">
                    {loading ? (
                        <div className="flex min-h-[280px] items-center justify-center">
                            <Loader />
                        </div>
                    ) : error ? (
                        <div className="flex min-h-[280px] items-center justify-center px-4 text-center">
                            <p className="text-white/60 text-[14px]" style={{ fontFamily: BODY }}>
                                {error}
                            </p>
                        </div>
                    ) : !level ? (
                        <div className="flex min-h-[280px] items-center justify-center px-4 text-center">
                            <p className="text-white/60 text-[14px]" style={{ fontFamily: BODY }}>
                                Схема зала недоступна
                            </p>
                        </div>
                    ) : (
                        <>
                            <Hall
                                level={level}
                                selectedSeats={highlightedSeats}
                                readOnly
                                prices={sessionData.prices}
                                spiderMan={spiderMan}
                            />
                            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                                {sessionData.prices.map((item, index) => (
                                    <div className="flex items-center gap-1.5" key={index}>
                                        <div style={{ backgroundColor: item.color }} className="h-4 w-4 rounded-[4px]" />
                                        <span className="text-white/70 text-[11px]" style={{ fontFamily: MONO }}>
                                            {item.name}
                                        </span>
                                    </div>
                                ))}
                                <div className="flex items-center gap-1.5">
                                    {spiderMan ? (
                                        <SpiderManSeatIcon variant="selected" className="h-5 w-5 shrink-0" />
                                    ) : (
                                        <div className="bg-accent h-4 w-4 rounded-[4px]" />
                                    )}
                                    <span className="text-white/70 text-[11px]" style={{ fontFamily: MONO }}>
                                        Ваши места
                                    </span>
                                </div>
                                {spiderMan && (
                                    <div className="flex items-center gap-1.5">
                                        <SpiderManSeatIcon variant="occupied" className="h-5 w-5 shrink-0" />
                                        <span className="text-white/70 text-[11px]" style={{ fontFamily: MONO }}>
                                            Занятые места
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </motion.div>
        </motion.div>,
        document.body,
    )
}
